import {useEffect, useMemo, useState} from 'react';
import {useMutation, useQuery} from '@apollo/client';
import {Button, Modal, Spin, Tag} from 'antd';
import {DeleteOutlined, ExclamationCircleOutlined} from '@ant-design/icons';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {makeSelectTabOperations} from 'redux/store/auth';
import {NavTabsEnum} from 'utils/constants';
import SubscriptionBundleModal from '../components/SubscriptionBundleModal';
import SubscriptionResourceStatusModal from '../components/SubscriptionResourceStatusModal';
import {formatMoney, humanizeValue} from '../subscriptions/BundleHelpers';
import {
  CHANGE_SUBSCRIPTION_BUNDLE_STATUS,
  DELETE_SUBSCRIPTION_BUNDLE,
  GET_SUBSCRIPTION_BUNDLES,
} from '../resolvers';
import styles from '../subscriptionSettings.module.scss';

const SubscriptionBundlesTab = () => {
  const [modalState, setModalState] = useState({
    open: false,
    bundleData: null,
  });
  const [statusModalState, setStatusModalState] = useState({
    open: false,
    bundleData: null,
  });

  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.Subscription),
  );

  const {data, loading, refetch} = useQuery(GET_SUBSCRIPTION_BUNDLES, {
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });

  const [deleteBundle, {loading: deleting}] = useMutation(
    DELETE_SUBSCRIPTION_BUNDLE,
    {
      errorPolicy: 'all',
      onError: err => toast.error(err.message),
    },
  );

  const handleDelete = async key => {
    const {data: result} = await deleteBundle({variables: {key}});
    const payload = result?.deleteSubscriptionBundle;
    if (!payload) return;
    if (payload.__typename === 'Error') {
      toast.error(payload.message || 'Failed to delete bundle');
      return;
    }
    toast.success(payload.message || 'Bundle deleted successfully');
    refetch();
  };

  const response = data?.getDabaSubscriptionBundlesAdmin;

  useEffect(() => {
    if (response?.__typename === 'Error') {
      toast.error(response.message);
    }
  }, [response]);

  const bundles =
    response?.__typename === 'ResponseWithDabaSubscriptionBundles'
      ? response?.data || []
      : [];

  const bundleCards = useMemo(
    () =>
      bundles.map(bundle => ({
        key: bundle?.key,
        name: bundle?.name,
        language: bundle?.language,
        description: bundle?.description,
        isVisibleToUsers: Boolean(bundle?.isVisibleToUsers),
        isEnabled: Boolean(bundle?.isEnabled),
        data: bundle,
      })),
    [bundles],
  );

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spin size="large" />
        <h1>Loading...</h1>
      </div>
    );
  }

  return (
    <div className={styles.content}>
      <div className={styles['plans-header']}>
        <h2>Subscription Bundles</h2>
        <Button
          type="primary"
          disabled={!tabOperations?.create}
          onClick={() => setModalState({open: true, bundleData: null})}>
          Create Bundle
        </Button>
      </div>

      {!bundleCards.length ? (
        <div className={styles['empty-state']}>
          No subscription bundles found.
        </div>
      ) : (
        <div className={styles['bundle-grid']}>
          {bundleCards.map(bundle => (
            <article
              key={bundle.key || bundle.name}
              className={styles['bundle-card']}>
              <div className={styles['bundle-card-head']}>
                <div>
                  <h3>{bundle.name || '-'}</h3>
                  <p className={styles['bundle-key-chip']}>
                    {bundle.key || '-'}
                  </p>
                </div>
                <div className={styles['bundle-head-actions']}>
                  <Button
                    type="link"
                    className={styles['bundle-edit-btn']}
                    disabled={!tabOperations?.update || !bundle.key}
                    onClick={() =>
                      setStatusModalState({
                        open: true,
                        bundleData: bundle.data,
                      })
                    }>
                    Change Status
                  </Button>
                  <Button
                    type="link"
                    className={styles['bundle-edit-btn']}
                    disabled={!tabOperations?.update}
                    onClick={() =>
                      setModalState({
                        open: true,
                        bundleData: bundle.data,
                      })
                    }>
                    Edit
                  </Button>
                  <Button
                    type="link"
                    danger
                    icon={<DeleteOutlined />}
                    disabled={!tabOperations?.delete || !bundle.key}
                    onClick={() =>
                      Modal.confirm({
                        title: 'Delete this bundle?',
                        icon: <ExclamationCircleOutlined />,
                        content: 'This action cannot be undone.',
                        okText: 'Delete',

                        okButtonProps: {danger: true, loading: deleting},
                        cancelText: 'Cancel',
                        onOk: () => handleDelete(bundle.key),
                      })
                    }>
                    Delete
                  </Button>
                </div>
              </div>

              <p className={styles['bundle-description']}>
                {bundle.description || '-'}
              </p>

              <div className={styles['bundle-tags']}>
                <Tag color={bundle.isEnabled ? 'green' : 'red'}>
                  {bundle.isEnabled ? 'Enabled' : 'Disabled'}
                </Tag>
              </div>

              <div className={styles['bundle-detail-grid']}>
                <div className={styles['bundle-detail-card']}>
                  <h4>
                    Items <span>{bundle.data?.items?.length || 0}</span>
                  </h4>
                  <div className={styles['bundle-item-list']}>
                    {(bundle.data?.items || []).length ? (
                      (bundle.data?.items || []).map(item => (
                        <span
                          key={item?.catalogKey}
                          className={styles['bundle-item-chip']}>
                          {humanizeValue(item?.catalogItem?.plan)}
                          {' - '}
                          {humanizeValue(item?.catalogItem?.subscriptionType)}
                        </span>
                      ))
                    ) : (
                      <span className={styles['bundle-empty-line']}>
                        No items
                      </span>
                    )}
                  </div>
                </div>
                <div className={styles['bundle-detail-card']}>
                  <h4>
                    Prices <span>{bundle.data?.prices?.length || 0}</span>
                  </h4>
                  <div className={styles['bundle-price-list']}>
                    {(bundle.data?.prices || []).length ? (
                      (bundle.data?.prices || []).map((price, index) => (
                        <span
                          key={`${price?.currency}-${price?.amount}-${index}`}
                          className={styles['bundle-price-chip']}>
                          {formatMoney(price?.amount, price?.currency)}
                        </span>
                      ))
                    ) : (
                      <span className={styles['bundle-empty-line']}>
                        No prices
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      <SubscriptionBundleModal
        isOpen={modalState.open}
        onClose={() => setModalState({open: false, bundleData: null})}
        bundleData={modalState.bundleData}
        refetch={refetch}
        canSubmit={
          modalState.bundleData ? tabOperations?.update : tabOperations?.create
        }
      />
      <SubscriptionResourceStatusModal
        isOpen={statusModalState.open}
        onClose={() => setStatusModalState({open: false, bundleData: null})}
        resourceData={statusModalState.bundleData}
        refetch={refetch}
        canSubmit={tabOperations?.update}
        mutation={CHANGE_SUBSCRIPTION_BUNDLE_STATUS}
        mutationName="changeSubscriptionBundleStatus"
        successTypename="ResponseWithSubscriptionBundle"
        resourceLabel="Bundle"
      />
    </div>
  );
};

export default SubscriptionBundlesTab;
